<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :closable="false"
    :maskClosable="false"
    width="860px"
    :bodyStyle="{ padding: 0, borderRadius: '8px' }"
    @cancel="close"
  >
    <div class="ref-gal">
      <div class="ref-gal-head">
        <div class="ref-gal-title">参考图库</div>
        <div class="ref-gal-close" @click="close">×</div>
      </div>
      <div class="ref-gal-tabs">
        <div class="ref-gal-tab" :class="{ on: activeTab === 'common' }" @click="switchTab('common')">参考图公库</div>
        <div class="ref-gal-tab" :class="{ on: activeTab === 'slas' }" @click="switchTab('slas')">参考图私库</div>
      </div>
      <div class="ref-gal-body">
        <div class="ref-gal-left">
          <template v-if="activeTab === 'common'">
            <div v-for="node in styleTree" :key="'p-' + node.id" class="style-block">
              <div
                class="cat-item"
                :class="{ on: selectedStyleId === node.id }"
                @click="selectStyle(node)"
              >
                <span class="cat-name">{{ node.name }}</span>
                <span class="cat-num" v-if="node.count != null">{{ node.count }}</span>
              </div>
              <div
                v-for="child in node.children || []"
                :key="'c-' + child.id"
                class="cat-item child"
                :class="{ on: selectedStyleId === child.id }"
                @click="selectStyle(child)"
              >
                <span class="cat-name">{{ child.name }}</span>
                <span class="cat-num" v-if="child.count != null">{{ child.count }}</span>
              </div>
            </div>
            <div v-if="!styleTree.length" class="cat-empty">暂无风格分类</div>
          </template>
          <template v-else>
            <div
              v-for="g in privateGroups"
              :key="g.name"
              class="cat-item"
              :class="{ on: selectedGroup === g.name }"
              @click="selectGroup(g.name)"
            >
              <span class="cat-name">{{ g.name }}</span>
              <span class="cat-num">{{ g.count }}</span>
            </div>
            <div v-if="!privateGroups.length" class="cat-empty">暂无私库分组</div>
          </template>
        </div>
        <div class="ref-gal-right">
          <div class="pic-grid">
            <div
              v-for="item in displayPics"
              :key="item.id || item.picUrl"
              class="pic-card"
              :class="{ on: item.select }"
              @click="toggleSelect(item)"
            >
              <img :src="item.thumbUrl || item.picUrl" loading="lazy" @error="onImgError(item)" />
              <div v-if="item.select" class="pic-badge">✓</div>
            </div>
          </div>
          <div v-if="!displayPics.length" class="pic-empty">暂无参考图</div>
          <div v-if="activeTab === 'common'" class="pic-pager">
            <a-pagination
              size="small"
              :total="commonTotal"
              :pageSize="commonPageSize"
              :current="commonPageNo"
              @change="onPageChange"
            />
          </div>
        </div>
      </div>
      <div class="ref-gal-foot">
        <div class="ref-gal-cancel" @click="close">取消</div>
        <div class="ref-gal-ok" :class="{ disabled: !selectedCount }" @click="confirm">
          确定{{ selectedCount ? '(' + selectedCount + ')' : '' }}
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message } from 'ant-design-vue'
import { requestHttpGet } from '@/api/main'
import apiUrl from '@/utils/config'

const DEFAULT_GROUPS = ['家装效果图', '工装效果图', '商品']

export default {
  name: 'ReferenceGallery',
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      activeTab: 'common',
      styleTree: [],
      selectedStyleId: null,
      privateGroups: [],
      selectedGroup: '',
      commonPics: [],
      slasPics: [],
      allSlasPics: [],
      commonPageNo: 1,
      commonPageSize: 30,
      commonTotal: 0,
      loading: false,
    }
  },
  computed: {
    displayPics() {
      return this.activeTab === 'common' ? this.commonPics : this.slasPics
    },
    selectedCount() {
      return this.displayPics.filter((e) => e.select).length
    },
  },
  watch: {
    visible(v) {
      if (v) {
        this.activeTab = 'common'
        this.init()
      }
    },
  },
  methods: {
    close() {
      this.$emit('close')
    },
    onImgError(item) {
      if (item.thumbUrl) item.thumbUrl = ''
    },
    async init() {
      await Promise.all([this.loadStyles(), this.loadSlas()])
      await this.loadCommon()
    },
    switchTab(tab) {
      this.activeTab = tab
      this.clearSelect()
      if (tab === 'common') {
        this.loadCommon()
      } else {
        this.filterSlas()
      }
    },
    clearSelect() {
      this.commonPics.forEach((e) => { e.select = false })
      this.slasPics.forEach((e) => { e.select = false })
    },
    buildTree(list) {
      const map = {}
      const roots = []
      ;(list || []).forEach((e) => {
        map[e.id] = { ...e, children: [] }
      })
      Object.values(map).forEach((node) => {
        if (node.parentId && map[node.parentId]) {
          map[node.parentId].children.push(node)
        } else {
          roots.push(node)
        }
      })
      return roots
    },
    async loadStyles() {
      try {
        const res = await requestHttpGet(apiUrl + '/admin-api/product/style/list-all-simple', {})
        this.styleTree = this.buildTree((res && res.data) || [])
        if (!this.selectedStyleId && this.styleTree.length) {
          const first = this.styleTree[0]
          const leaf = (first.children && first.children[0]) || first
          this.selectedStyleId = leaf.id
        }
      } catch (e) {
        console.error(e)
        this.styleTree = []
      }
    },
    async loadCommon() {
      this.loading = true
      try {
        const params = {
          pageNo: this.commonPageNo,
          pageSize: this.commonPageSize,
        }
        if (this.selectedStyleId) {
          params.styleId = this.selectedStyleId
        }
        const res = await requestHttpGet(apiUrl + '/admin-api/product/reference/common-page', params)
        const data = (res && res.data) || {}
        this.commonTotal = Number(data.total) || 0
        this.commonPics = (data.list || []).map((e) => ({ ...e, select: false }))
      } catch (e) {
        console.error(e)
        this.commonPics = []
        this.commonTotal = 0
      } finally {
        this.loading = false
      }
    },
    async loadSlas() {
      try {
        const res = await requestHttpGet(apiUrl + '/admin-api/product/reference/slas')
        this.allSlasPics = ((res && res.data) || []).map((e) => ({ ...e, select: false }))
        const countMap = {}
        this.allSlasPics.forEach((e) => {
          const name = e.referenceName || '未分组'
          countMap[name] = (countMap[name] || 0) + 1
        })
        const names = [...DEFAULT_GROUPS]
        Object.keys(countMap).forEach((n) => {
          if (!names.includes(n)) names.push(n)
        })
        this.privateGroups = names
          .filter((n) => countMap[n] || DEFAULT_GROUPS.includes(n))
          .map((name) => ({ name, count: countMap[name] || 0 }))
          .filter((g) => g.count > 0 || DEFAULT_GROUPS.includes(g.name))
        // 优先选有图的分组
        const withPics = this.privateGroups.find((g) => g.count > 0)
        this.selectedGroup = (withPics && withPics.name) || (this.privateGroups[0] && this.privateGroups[0].name) || ''
        this.filterSlas()
      } catch (e) {
        console.error(e)
        this.allSlasPics = []
        this.privateGroups = DEFAULT_GROUPS.map((name) => ({ name, count: 0 }))
        this.selectedGroup = DEFAULT_GROUPS[0]
        this.slasPics = []
      }
    },
    filterSlas() {
      this.slasPics = this.allSlasPics
        .filter((e) => (e.referenceName || '未分组') === this.selectedGroup)
        .map((e) => ({ ...e, select: false }))
    },
    selectStyle(node) {
      this.selectedStyleId = node.id
      this.commonPageNo = 1
      this.loadCommon()
    },
    selectGroup(name) {
      this.selectedGroup = name
      this.filterSlas()
    },
    onPageChange(page) {
      this.commonPageNo = page
      this.loadCommon()
    },
    toggleSelect(item) {
      item.select = !item.select
      this.$forceUpdate()
    },
    confirm() {
      const pics = this.displayPics.filter((e) => e.select).map((e) => e.picUrl).filter(Boolean)
      if (!pics.length) {
        message.warning('请选择参考图')
        return
      }
      this.$emit('ok', { pics })
      this.close()
    },
  },
}
</script>

<style scoped>
.ref-gal {
  background: #fff;
}
.ref-gal-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 8px;
}
.ref-gal-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
}
.ref-gal-close {
  width: 28px;
  height: 28px;
  line-height: 26px;
  text-align: center;
  font-size: 22px;
  color: #999;
  cursor: pointer;
}
.ref-gal-tabs {
  display: flex;
  padding: 0 20px;
  border-bottom: 1px solid #eee;
}
.ref-gal-tab {
  margin-right: 28px;
  padding: 10px 0;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  position: relative;
}
.ref-gal-tab.on {
  color: #ff4040;
  font-weight: 600;
}
.ref-gal-tab.on::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  background: #ff4040;
}
.ref-gal-body {
  display: flex;
  height: 420px;
  border-bottom: 1px solid #eee;
}
.ref-gal-left {
  width: 200px;
  border-right: 1px solid #eee;
  overflow-y: auto;
  padding: 8px 0;
}
.cat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}
.cat-item.child {
  padding-left: 28px;
  color: #555;
}
.cat-item.on {
  background: rgba(255, 64, 64, 0.08);
  color: #ff4040;
}
.cat-num {
  color: #999;
  font-size: 12px;
}
.cat-item.on .cat-num {
  color: #ff4040;
}
.cat-empty {
  padding: 20px 16px;
  color: #999;
  font-size: 13px;
}
.ref-gal-right {
  flex: 1;
  padding: 12px 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.pic-grid {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 12px;
}
.pic-card {
  width: 140px;
  height: 105px;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  border: 2px solid transparent;
  box-sizing: border-box;
}
.pic-card.on {
  border-color: #ff4040;
}
.pic-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.pic-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ff4040;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
}
.pic-empty {
  color: #999;
  text-align: center;
  padding: 60px 0;
}
.pic-pager {
  padding-top: 8px;
  text-align: center;
}
.ref-gal-foot {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
  align-items: center;
  padding: 14px 20px;
  gap: 12px;
  white-space: nowrap;
}
.ref-gal-cancel {
  flex: 0 0 auto;
  min-width: 72px;
  height: 34px;
  line-height: 32px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  padding: 0 16px;
  background: #fff;
  white-space: nowrap;
}
.ref-gal-ok {
  flex: 0 0 auto;
  min-width: 72px;
  height: 34px;
  line-height: 34px;
  text-align: center;
  background: #ff4040;
  color: #fff;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  padding: 0 16px;
  white-space: nowrap;
}
.ref-gal-ok.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
